"use client";

import type { ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useBreakpoint } from "@/hooks/use-mobile-layout";
import { useMobileBackLayer } from "@/studio/components/MobileBackStackHost";
import { useStudioMenuBlur } from "@/studio/lib/studioMenuBlur";
import { StudioIconButton } from "../StudioIconButton";
import "./overlays.css";

/** Centered dialog on desktop. Bottom sheet below md. Same card blur veil as context menus. */
export function StudioDialog({
  open,
  onClose,
  title,
  children,
  showClose = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Confirm dialogs use Cancel — hide the head X by default there. */
  showClose?: boolean;
}) {
  const { isMobile } = useBreakpoint();
  useMobileBackLayer(`dialog:${title}`, open, onClose);
  useStudioMenuBlur(open);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="studio-overlay-dialog-backdrop" />
        <DialogPrimitive.Content
          data-studio-overlay=""
          className={cn(
            "studio-overlay-dialog-content",
            isMobile ? "studio-overlay-sheet" : "studio-overlay-dialog",
          )}
        >
          <header
            className={cn("studio-overlay-dialog-head", !showClose && "is-no-close")}
          >
            <DialogPrimitive.Title className="studio-overlay-dialog-title">{title}</DialogPrimitive.Title>
            {showClose ? (
              <DialogPrimitive.Close asChild>
                <StudioIconButton className="studio-overlay-dialog-close" aria-label="Close">
                  <X size={16} strokeWidth={2.25} aria-hidden="true" />
                </StudioIconButton>
              </DialogPrimitive.Close>
            ) : null}
          </header>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
